import { Injectable } from '@angular/core';
import { UsageAnalyticsService } from '@shared/services/usage-analytics/usage-analytics.service';
import { Ticket, TicketComment } from '@shared/services';

export enum TicketTrackingEvent {
  OPENED = 'ActionTicket Opened',
  APPROVED = 'ActionTicket Approved',
  EMAILED = 'ActionTicket Emailed',
  FORWARD = 'ActionTicket Forward',
  STATUS_MOVED = 'ActionTicket StatusMoved',
  CATEGORY_MOVED = 'ActionTicket CategoryMoved',
  ALERT_CATEGORY_MOVED = 'AlertTicket CategoryMoved',
  CREATED = 'ActionTicket Created',
  UPDATED = 'ActionTicket Updated',
  ARCHIVED = 'ActionTicket Archived',
  UNARCHIVED = 'ActionTicket Unarchived',
  DELETED = 'ActionTicket Deleted',
  ASSIGN = 'ActionTicket Assign',
  UPLOAD_ATTACHMENT = 'ActionTicket UploadAttachment',
  SHOW_ACTIVITY = 'ActionTicket ShowActivity',
  HIDE_ACTIVITY = 'ActionTicket HideActivity',
  ACTION_SHARED = 'ActionTicket Shared'
}


export enum TicketCommentTrackingEvent {
  EDITED = 'ActionComment Edited',
  CREATED = 'ActionComment Created',
  ANONYMOUS_CREATED = 'ActionCommentAnonymous Created'
}


export type TicketTrackingField = 'ticket_id' | 'ticket_name' | 'site_id' | 'status_from' | 'status_to' | 'category_to' | 'image_name_arr' | 'type';
export type TicketCommentTrackingField = 'comment_id' | 'comment_author_name';
export type TicketExportTrackingField = 'method';
export type TrackingField = TicketTrackingField | TicketCommentTrackingField | TicketExportTrackingField;
export type TrackingProperties = { [key in TrackingField]?: any };


@Injectable({
  providedIn: 'root'
})
export class TicketTrackingService {

  private readonly DEFAULT_TICKET_TRACKING_FIELDS: TicketTrackingField[] = ['ticket_id', 'ticket_name', 'site_id'];
  private readonly DEFAULT_TICKET_COMMENT_TRACKING_FIELDS: TicketCommentTrackingField[] = ['comment_id', 'comment_author_name'];

  constructor(private usageAnalyticsService: UsageAnalyticsService) {
  }

  trackTicket(event: TicketTrackingEvent, ticket: Ticket, extraProperties?: TrackingProperties) {
    this.track(event, {
      ...this.getValuesByFields(ticket, this.DEFAULT_TICKET_TRACKING_FIELDS, this.getTicketFieldValue),
      ...extraProperties,
    });
  }

  trackComment(event: TicketCommentTrackingEvent, ticket: Ticket, comment: TicketComment, extraProperties?: TrackingProperties) {
    this.track(event, {
      ...this.getValuesByFields(ticket, this.DEFAULT_TICKET_TRACKING_FIELDS, this.getTicketFieldValue),
      ...this.getValuesByFields(comment, this.DEFAULT_TICKET_COMMENT_TRACKING_FIELDS, this.getTicketCommentFieldValue),
      ...extraProperties
    });
  }

  private track(event: TicketTrackingEvent | TicketCommentTrackingEvent, properties: TrackingProperties) {
    this.usageAnalyticsService.trackAndIncludeUserEmail(event.valueOf(), properties);
  }

  private getValuesByFields(object: Ticket | TicketComment,
                            fields: TrackingField[],
                            getFn: (object: Ticket | TicketComment, field: TrackingField) => any) {
    return object ? fields.reduce((props, field) => {
      const value = getFn(object, field);
      if (value) {
        props[field] = value;
      }
      return props;
    }, {}) : null;
  }

  private getTicketFieldValue(ticket: Ticket, field: TicketTrackingField): string | number {
    switch (field) {
      case 'ticket_name':
        return ticket.summary;
      case 'site_id':
        return ticket.site_id;
      default:
        return ticket[field];
    }
  }

  private getTicketCommentFieldValue(comment: TicketComment, field: TicketCommentTrackingField): string {
    switch (field) {
      case 'comment_id':
        return comment.comment_id;
      case 'comment_author_name':
        return comment.author_id ? null : comment.author_full_name;
      default:
        return comment[field];
    }
  }

}
